import React from 'react';
import PropTypes from 'prop-types';
import { TextInput, TouchableWithoutFeedback } from 'react-native';
import { Box, Inline } from '@styleseat/ui';
import { colors } from '@styleseat/brand';
import { Button, Touchable } from '../../../ui';
import { BasicSearchIcon, CloseIcon } from '../../../ui/Icons';
import './SearchInput.module.scss';
import { clickable } from '../../../../styles';

/**
 * Search Input Component
 *
 * Shows a text field where the user can type in a search query
 * - it may be configured to show the "Search" button within the field.
 */
export default class SearchInput extends React.Component {
  inputRef = React.createRef();

  static propTypes = {
    value: PropTypes.string,
    showSearchButton: PropTypes.bool,
    autoFocus: PropTypes.bool,

    onChangeText: PropTypes.func,
    onEndEditing: PropTypes.func,
    onSearch: PropTypes.func,
    onFocus: PropTypes.func,
    onBlur: PropTypes.func,
    onClearQueryValue: PropTypes.func,
  };

  static defaultProps = {
    value: '',
    showSearchButton: false,
    autoFocus: false,
    onChangeText: () => {},
    onEndEditing: () => {},
    onSearch: () => {},
    onFocus: () => {},
    onBlur: () => {},
    onClearQueryValue: null,
  };

  state = {
    focused: false,
  };

  handleChangeText = newValue => {
    this.props.onChangeText(newValue);
  };

  handleSearchClick = () => {
    this.props.onSearch(this.props.value);
  };

  handleInputFocus = () => {
    this.setState({ focused: true });
    this.props.onFocus();
  };

  handleInputBlur = () => {
    this.setState({ focused: false });
    this.props.onBlur();
  };

  /**
   * Proxy to the TextInput "focus" fn
   * when called it will focus the text input
   */
  focus = () => {
    const { inputRef } = this;
    return inputRef.current && inputRef.current.focus();
  };

  render() {
    const {
      value,
      showSearchButton,
      autoFocus,
      onEndEditing,
      onClearQueryValue,
    } = this.props;
    const { focused } = this.state;
    const { inputRef } = this;

    return (
      <Inline
        alignItems="center"
        justifyContent="center"
        position="relative"
        paddingLeft={1}
        minHeight={40}
        color={colors.whiteWash}
        borderColor={focused ? colors.blackInBlack : colors.whiteWash}
        borderStyle="solid"
        borderWidth={1}
        borderRadius={20}
      >
        <TouchableWithoutFeedback onPress={() => this.focus()}>
          <Box paddingX={8} style={clickable}>
            <BasicSearchIcon width={20} height={20} />
          </Box>
        </TouchableWithoutFeedback>

        <TextInput
          autoFocus={autoFocus}
          value={value || ''}
          ref={inputRef}
          styleName="text-input"
          onChangeText={this.handleChangeText}
          placeholder="Service, Stylist or Salon"
          clearButtonMode="while-editing"
          onFocus={this.handleInputFocus}
          onBlur={this.handleInputBlur}
          onEndEditing={onEndEditing}
          keyboardType="web-search"
          testID="search-text-input"
        />

        {value?.length > 0 && onClearQueryValue ? (
          <Touchable onClick={onClearQueryValue}>
            <Box paddingX={8} color={colors.transparent}>
              <CloseIcon width={12} height={12} color={colors.silver} />
            </Box>
          </Touchable>
        ) : (
          <Box
            paddingX={8}
            color={colors.transparent}
            minWidth={20}
            minHeight={20}
          />
        )}

        {showSearchButton && (
          <Button
            appearance="primary"
            size="medium"
            styleName="search-button"
            onPress={this.handleSearchClick}
          >
            Search
          </Button>
        )}
      </Inline>
    );
  }
}
